import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Alert,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import RefreshIcon from '@mui/icons-material/Refresh';

const PromptSuggestions = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<any>(null);
  const [timeframe, setTimeframe] = useState(7);

  const fetchSuggestions = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`/api/prompt-suggestions/suggestions?days=${timeframe}`);
      if (!response.ok) throw new Error('Failed to fetch suggestions');
      const data = await response.json();
      setSuggestions(data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSuggestions();
  }, [timeframe]);

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'error';
      case 'high': return 'warning';
      case 'moderate': return 'info';
      default: return 'default';
    }
  };

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'critical': return <ErrorIcon />;
      case 'high': return <WarningIcon />;
      default: return <InfoIcon />;
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        {error}
        <Button onClick={fetchSuggestions} size="small" sx={{ ml: 2 }}>
          Retry
        </Button>
      </Alert>
    );
  }

  if (!suggestions || suggestions.suggestions.length === 0) {
    return (
      <Card>
        <CardContent>
          <Alert severity="success" icon={<LightbulbIcon />}>
            <Typography variant="h6">No Prompt Improvements Needed</Typography>
            <Typography variant="body2">
              {suggestions?.summary || 'All calls are performing well with no significant issues detected.'}
            </Typography>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Box>
      {/* Header Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h5" component="h2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LightbulbIcon color="primary" />
              AI Prompt Improvement Suggestions
            </Typography>
            <Box display="flex" gap={2} alignItems="center">
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Timeframe</InputLabel>
                <Select
                  value={timeframe}
                  label="Timeframe"
                  onChange={(e) => setTimeframe(Number(e.target.value))}
                >
                  <MenuItem value={1}>Last 24 hours</MenuItem>
                  <MenuItem value={3}>Last 3 days</MenuItem>
                  <MenuItem value={7}>Last 7 days</MenuItem>
                  <MenuItem value={14}>Last 14 days</MenuItem>
                  <MenuItem value={30}>Last 30 days</MenuItem>
                </Select>
              </FormControl>
              <Button
                startIcon={<RefreshIcon />}
                onClick={fetchSuggestions}
                variant="outlined"
                size="small"
              >
                Refresh
              </Button>
            </Box>
          </Box>

          <Alert severity="info" sx={{ mb: 2 }}>
            {suggestions.summary}
          </Alert>

          <Box display="flex" gap={3} flexWrap="wrap">
            <Box>
              <Typography variant="body2" color="text.secondary">Total Calls Analyzed</Typography>
              <Typography variant="h6">{suggestions.total_calls_analyzed}</Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">Average Grade</Typography>
              <Typography variant="h6">{suggestions.average_grade}/10</Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">Low Grade Calls</Typography>
              <Typography variant="h6">{suggestions.calls_with_low_grades}</Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">Critical Issues</Typography>
              <Typography variant="h6" color="error">{suggestions.calls_with_critical_issues}</Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Suggestions List */}
      {suggestions.suggestions.map((suggestion: any, index: number) => (
        <Accordion key={index} defaultExpanded={index === 0}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box display="flex" alignItems="center" gap={2} width="100%">
              {getSeverityIcon(suggestion.severity)}
              <Box flex={1}>
                <Typography variant="h6" component="div">
                  {suggestion.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {suggestion.description}
                </Typography>
              </Box>
              <Box display="flex" gap={1} alignItems="center">
                <Chip
                  label={suggestion.severity.toUpperCase()}
                  color={getSeverityColor(suggestion.severity)}
                  size="small"
                />
                <Chip
                  label={`${suggestion.frequency} calls (${suggestion.percentage}%)`}
                  size="small"
                  variant="outlined"
                />
              </Box>
            </Box>
          </AccordionSummary>

          <AccordionDetails>
            <Box>
              {/* Prompt Section */}
              <Box mb={2}>
                <Typography variant="subtitle2" color="primary" gutterBottom>
                  📍 Prompt Section to Update:
                </Typography>
                <Chip label={suggestion.prompt_section} variant="outlined" />
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Impact */}
              <Box mb={2}>
                <Typography variant="subtitle2" color="error" gutterBottom>
                  ⚠️ Impact:
                </Typography>
                <Typography variant="body2">{suggestion.impact}</Typography>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Suggested Fix */}
              <Box mb={2}>
                <Typography variant="subtitle2" color="success.main" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LightbulbIcon fontSize="small" />
                  Suggested Prompt Update:
                </Typography>
                <Box
                  component="pre"
                  sx={{
                    bgcolor: 'grey.100',
                    p: 2,
                    borderRadius: 1,
                    overflow: 'auto',
                    fontSize: '0.875rem',
                    fontFamily: 'monospace',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word'
                  }}
                >
                  {suggestion.suggested_fix}
                </Box>
              </Box>

              {/* Examples */}
              {suggestion.examples && suggestion.examples.length > 0 && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      📋 Example Calls:
                    </Typography>
                    <List dense>
                      {suggestion.examples.map((example: any, idx: number) => (
                        <ListItem key={idx} sx={{ bgcolor: 'grey.50', mb: 1, borderRadius: 1 }}>
                          <ListItemText
                            primary={
                              <Typography variant="body2" fontWeight="bold">
                                Call: {example.call_id?.substring(5, 20)}...
                              </Typography>
                            }
                            secondary={
                              <>
                                <Typography variant="body2" component="div" sx={{ mt: 1 }}>
                                  {example.description}
                                </Typography>
                                {example.quote && (
                                  <Typography variant="body2" component="div" sx={{ mt: 1, fontStyle: 'italic', color: 'text.secondary' }}>
                                    "{example.quote}"
                                  </Typography>
                                )}
                              </>
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                </>
              )}

              {/* Action Buttons */}
              <Box mt={3} display="flex" gap={2}>
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  onClick={() => {
                    // Copy suggested fix to clipboard
                    navigator.clipboard.writeText(suggestion.suggested_fix);
                  }}
                >
                  Copy Fix to Clipboard
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    // Open prompt file (this would need to be implemented based on your setup)
                    alert(`Open ${suggestion.prompt_section} section in your prompt editor`);
                  }}
                >
                  View Prompt Section
                </Button>
              </Box>
            </Box>
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
};

export default PromptSuggestions;


